import React, { useContext, useEffect, useState } from "react";
import { apiGet, apiPost, apiPut } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import Pagination from "../Pagination";
import AuthContext from "context/AuthContext";
import { useTranslation } from "react-i18next";
import AreaModal from "./areaModal";
import { useLocation } from "react-router-dom";
import helper from "utils/helpers";
import useToastContext from "hooks/useToastContext";
import DynamicTable from "../../components/DynamicTable";
import Select from "react-select";

function Areas() {
  const { t } = useTranslation();
  const location = useLocation();
  const notification = useToastContext();
  const { logoutUser, user, updatePageName } = useContext(AuthContext);
  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10,
  });

  const [areas, setAllAreas] = useState([]);
  const [page, setPage] = useState(
    location?.state?.page ? location?.state?.page : 1
  );
  const [pageSize] = useState(10);
  const [item, setItem] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [sampleFilePath, setSampleFilePath] = useState("");
  const [filterData, setFilterData] = useState({
    category: "",
    searchkey: "",
    startDate: "",
    endDate: "",
    isReset: false,
    isFilter: false,
    districtId: "",
  });
  const [sort, setSort] = useState({
    sort_key: "createdAt",
    sort_type: "desc",
  });
  const [districts, setDistricts] = useState([]);
  const [districtSearch, setDistrictSearch] = useState("");
  const [filteredDistricts, setFilteredDistricts] = useState([]);
  const manager = user?.permission?.find((e) => e.manager === "area") ?? {};

  const getAllAreas = async (data) => {
    try {
      const { category, searchkey, isFilter, districtId } = filterData;
      if (
        (data?.deletePage && !(areas?.length > 1)) ||
        (isFilter && category && data?.statusChange && !(areas?.length > 1))
      ) {
        if (!(areas?.length > 1)) {
          setPage(page - 1);
          setIsDelete(true);
          setPaginationObj({ ...paginationObj, page: page - 1 });
        }
      } else {
        setIsDelete(false);
      }
      const payload = {
        page,
        pageSize,
        status: category,
        keyword: searchkey?.trim(),
        sort_key: sort.sort_key,
        sort_type: sort.sort_type,
        district_id: districtId,
      };

      const path = apiPath.adminAreaList;
      const result = await apiGet(path, payload);
      if (result?.status === 200) {
        const response = result?.data?.results;
        const resultStatus = result?.data?.success;
        console.log("response ", response);
        setAllAreas(response?.docs);
        setPaginationObj({
          ...paginationObj,
          page: response.page,
          pageCount: response.totalPages,
          perPageItem: response?.docs.length,
          totalItems: response.totalDocs,
        });
      }
    } catch (error) {
      console.log("error ", error);
      setPaginationObj({});
      if (error.response.status === 401 || error.response.status === 409) {
        logoutUser();
      }
    }
  };

  const getDistricts = async () => {
    try {
      const path = apiPath.districtList;
      const result = await apiGet(path);
      if (result?.status === 200) {
        setDistricts(result?.data?.results?.docs || []);
      }
    } catch (error) {
      console.log("error ", error);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setFilterData((prev) => ({
      ...prev,
      searchkey: value,
      isFilter: true,
    }));
  };

  const handleDistrictChange = (selectedOption) => {
    setFilterData((prev) => ({
      ...prev,
      districtId: selectedOption?.value || "",
      isFilter: true,
    }));
  };

  const handleReset = () => {
    setFilterData({
      category: "",
      searchkey: "",
      startDate: "",
      endDate: "",
      isReset: true,
      isFilter: false,
      districtId: "",
    });
  };

  const handleFileChange = async (e) => {
    const file = e?.target?.files[0];
    const isValidCSV = validateFiles(file);
    if (isValidCSV) {
      const formData = new FormData();
      formData.append("csvFile", file);
      const path = apiPath.uploadAreaCSVFile;
      const result = await apiPost(path, formData);
      if (result?.data?.success) {
        notification.success(result.data.message);
        getAllAreas();
      } else {
        notification.error(result.data.message);
      }
    }
  };

  const validateFiles = (uploadFile) => {
    let csvFileError = "";
    const supportedTypes = ["text/csv"];

    if (!uploadFile?.name?.endsWith(".xlsx")) {
      csvFileError = "Only xlsx files are supported.";
    }
    if (csvFileError) {
      alert(csvFileError);
      return false;
    } else {
      return true;
    }
  };

  const getSampleFilePath = async () => {
    try {
      const path = apiPath.downloadAreaSampleFile;
      const result = await apiGet(path);
      if (result?.status === 200) {
        const response = result?.data?.results;
        setSampleFilePath(response?.sampleFile);
      }
    } catch (error) {
      console.log("error ", error);
      setPaginationObj({});
      if (error.response.status === 401 || error.response.status === 409) {
        logoutUser();
      }
    }
  };

  const toggleModal = (item) => {
    setShowModal(!showModal);
    setItem(item ?? {});
  };

  const handleStatusChange = async (item) => {
    try {
      const payload = {
        status: item?.status === "inactive" ? "active" : "inactive",
      };
      const path = `${apiPath.updateAreaStatus}/${item?._id}`;
      const result = await apiPut(path, payload);
      if (result?.status === 200) {
        notification.success(result.data.message);
        getAllAreas();
      }
    } catch (error) {
      console.log("Error changing area status:", error.message);
    }
  };

  const handleDelete = async (item) => {
    try {
      const path = apiPath.deleteArea + "/" + item?._id;
      const result = await apiPut(path);
      if (result?.status === 200) {
        notification.success(result.data.message);
        getAllAreas();
      } else {
        notification.error(result.data.message);
      }
    } catch (error) {
      console.log("Error deleting area:", error.message);
    }
  };

  useEffect(() => {
    updatePageName("Area Manager");
    getAllAreas();
    getSampleFilePath();
    getDistricts();
  }, [page, sort, filterData]);

  useEffect(() => {
    if (districtSearch) {
      const filtered = districts.filter((district) =>
        district.name.toLowerCase().includes(districtSearch.toLowerCase())
      );
      setFilteredDistricts(filtered);
    } else {
      setFilteredDistricts(districts);
    }
  }, [districtSearch, districts]);

  const columns = [
    { key: "name", label: t("NAME"), sortable: true },
    {
      key: "createdAt",
      label: "Created At",
      sortable: true,
      render: (value) => new Date(value).toLocaleDateString(),
    },
    { key: "status", label: "Status", sortable: true },
  ];

  const actions = {
    view: true,
    edit: true,
    delete: true,
    onEdit: (area) => {
      toggleModal(area);
    },
    onDelete: (area) => {
      helper.alertFunction(
        `${t("ARE_YOU_SURE_TO_DELETE")} ${area.name}?`,
        area,
        handleDelete,
        true
      );
    },
  };
  console.log("paginationObj ", paginationObj);
  const handlePageClick = (event) => {
    const newPage = event.selected + 1;
    setPage(newPage);
  };

  return (
    <div>
      <div className="bg-[#F9F9F9]">
        <div className="px-3 py-4">
          <div className="bg-white border border-[#E9EDF9] rounded-lg">
            <div className="border-b-[#E3E3E3] grid 2xl:grid-cols-3 xl:grid-cols-2 lg:grid lg:grid-cols-1 gap-2 px-4 py-3">
              <div className="col-span-2 flex flex-wrap items-center gap-3">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center">
                    <input
                      type="file"
                      accept=".xlsx"
                      onChange={handleFileChange}
                      className="hidden"
                      id="file-upload"
                    />
                 
                  </div>
                  {sampleFilePath && (
                    <a
                      href={sampleFilePath}
                      download
                      className="bg-LightBlue flex text-sm px-8 py-2 rounded-lg items-center border border-transparent text-white hover:bg-DarkBlue whitespace-nowrap"
                    >
                      {t("DOWNLOAD_SAMPLE")}
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={filterData.searchkey}
                    onChange={handleSearch}
                    className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-LightBlue"
                  />
                  <div className="w-64">
                    <Select
                      value={
                        districts.find(
                          (d) => d.districtId === filterData.districtId
                        )
                          ? {
                              value: filterData.districtId,
                              label: districts.find(
                                (d) => d.districtId === filterData.districtId
                              ).name,
                            }
                          : null
                      }
                      onChange={handleDistrictChange}
                      onInputChange={(value) => setDistrictSearch(value)}
                      options={[
                        { value: "", label: "All Districts" },
                        ...filteredDistricts.map((district) => ({
                          value: district.districtId,
                          label: district.name,
                        })),
                      ]}
                      placeholder="Select District"
                      isClearable
                      className="basic-single"
                      classNamePrefix="select"
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderColor: "#E5E7EB",
                          "&:hover": {
                            borderColor: "#E5E7EB",
                          },
                          boxShadow: "none",
                          borderRadius: "0.5rem",
                          minHeight: "42px",
                        }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: state.isSelected
                            ? "#3B82F6"
                            : state.isFocused
                            ? "#EFF6FF"
                            : "white",
                          color: state.isSelected ? "white" : "#374151",
                          "&:hover": {
                            backgroundColor: state.isSelected
                              ? "#3B82F6"
                              : "#EFF6FF",
                          },
                        }),
                      }}
                    />
                  </div>
                  <button
                    onClick={handleReset}
                    className="bg-LightBlue text-white px-4 py-2 rounded-lg hover:bg-DarkBlue"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>
            <div className="p-3">
              <DynamicTable
                data={areas}
                columns={columns}
                sort={sort}
                setSort={setSort}
                actions={actions}
                page={page}
                manager={manager}
                userRole={user?.role}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
              />
              <div className="mt-4">
                {paginationObj?.totalItems ? (
                  <Pagination
                    handlePageClick={handlePageClick}
                    options={paginationObj}
                    isDelete={isDelete}
                    page={page}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
      {showModal && (
        <AreaModal
          showModal={showModal}
          toggleModal={toggleModal}
          item={item}
          getAllAreas={getAllAreas}
        />
      )}
    </div>
  );
}

export default Areas;
